import {
  doc, getDoc, updateDoc, arrayUnion, increment
} from "firebase/firestore";
import { database } from "../Firebase";

const PLAFOND_MOIS_PAR_CYCLE = 12;

// ── Calcule les mois offerts selon l'abonnement du filleul ───────────────────
export const getMoisOffertsPourParrain = (abonnementFilleul) => {
  if (abonnementFilleul === "spotyzen")  return 3; // annuel  → 3 mois
  if (abonnementFilleul === "spotyflex") return 1; // mensuel → 1 mois
  return 0;
};

export const getMoisOffertsPourFilleul = (abonnementFilleul) => {
  if (abonnementFilleul === "spotyzen") return 1; // annuel → 1 mois offert
  return 0;
};

// ── Ajoute des mois à une date d'expiration ───────────────────────────────────
const ajouterMois = (dateISO, nombreMois) => {
  const date = new Date(dateISO);
  date.setMonth(date.getMonth() + nombreMois);
  return date.toISOString();
};

// ── Applique les mois offerts au parrain ──────────────────────────────────────
export const appliquerMoisOffertsPourParrain = async ({
  parrainId,
  filleulId,
  filleulPseudo,
  abonnementFilleul,
  dateParrainage,
}) => {
  const moisAOffrir = getMoisOffertsPourParrain(abonnementFilleul);
  if (moisAOffrir === 0) return;

  const parrainSnap = await getDoc(doc(database, "users", parrainId));
  if (!parrainSnap.exists()) return;
  const parrain = parrainSnap.data();

  const moisCycleActuel  = parrain.moisOffertsCycle  || 0;
  const moisCredit       = parrain.moisPremiumCredit  || 0;
  const moisEnAttente    = parrain.moisPremiumEnAttente || 0;
  const estAbonne        = parrain.abonnementActif === true;
  const expiration       = parrain.abonnementExpiration;

  let moisAppliques  = 0;
  let moisEnCredit   = 0;
  let moisEnAttenteAjoutes = 0;
  let nouvelleExpiration   = expiration;
  let statutDetail         = "en_attente";

  if (!estAbonne) {
    // ── Pas abonné : tout va en attente ─────────────────────────────────────
    moisEnAttenteAjoutes = moisAOffrir;
    statutDetail         = "en_attente";
  } else {
    // ── Abonné : applique dans la limite du plafond ──────────────────────────
    const moisDisponiblesCycle = PLAFOND_MOIS_PAR_CYCLE - moisCycleActuel;

    if (moisDisponiblesCycle <= 0) {
      // Plafond atteint → tout en crédit
      moisEnCredit = moisAOffrir;
      statutDetail = "en_crédit";
    } else if (moisAOffrir <= moisDisponiblesCycle) {
      // Tout rentre dans le cycle actuel
      moisAppliques      = moisAOffrir;
      nouvelleExpiration = ajouterMois(expiration, moisAppliques);
      statutDetail       = "appliqué";
    } else {
      // Partiellement dans le cycle, reste en crédit
      moisAppliques      = moisDisponiblesCycle;
      moisEnCredit       = moisAOffrir - moisAppliques;
      nouvelleExpiration = ajouterMois(expiration, moisAppliques);
      statutDetail       = "partiel";
    }
  }

  // ── Construit le détail du parrainage ────────────────────────────────────
  const detailParrainage = {
    filleulId,
    filleulPseudo:     filleulPseudo || "Anonyme",
    dateParrainage:    dateParrainage || new Date().toISOString(),
    abonnementFilleul,
    moisOfferts:       moisAOffrir,
    moisAppliques,
    moisEnCredit,
    statut:            statutDetail,
  };

  // ── Mise à jour Firestore parrain ────────────────────────────────────────
  const updates = {
    totalMoisOfferts:     increment(moisAOffrir),
    moisOffertsCycle:     increment(moisAppliques),
    moisPremiumCredit:    increment(moisEnCredit),
    moisPremiumEnAttente: increment(moisEnAttenteAjoutes),
    parrainagesDetail:    arrayUnion(detailParrainage),
  };
  if (moisAppliques > 0) {
    updates.abonnementExpiration = nouvelleExpiration;
  }
  await updateDoc(doc(database, "users", parrainId), updates);

  return detailParrainage;
};

// ── Applique les mois offerts au filleul ──────────────────────────────────────
export const appliquerMoisOffertsPourFilleul = async ({
  filleulId,
  abonnementChoisi,
}) => {
  const moisAOffrir = getMoisOffertsPourFilleul(abonnementChoisi);
  if (moisAOffrir === 0) return;

  const filleulSnap = await getDoc(doc(database, "users", filleulId));
  if (!filleulSnap.exists()) return;
  const filleul = filleulSnap.data();

  const expiration       = filleul.abonnementExpiration;
  const nouvelleExpiration = ajouterMois(expiration, moisAOffrir);

  await updateDoc(doc(database, "users", filleulId), {
    abonnementExpiration: nouvelleExpiration,
    moisOffertFilleul:    moisAOffrir,
  });
};

// ── Active le crédit au renouvellement d'abonnement ──────────────────────────
export const activerCreditAuRenouvellement = async (userId) => {
  const userSnap = await getDoc(doc(database, "users", userId));
  if (!userSnap.exists()) return;
  const userData = userSnap.data();

  const moisCredit    = userData.moisPremiumCredit   || 0;
  const moisEnAttente = userData.moisPremiumEnAttente || 0;
  const totalDisponible = moisCredit + moisEnAttente;

  if (totalDisponible === 0) return;

  const expiration = userData.abonnementExpiration;

  // ── Applique dans la limite du plafond du nouveau cycle ──────────────────
  const moisAAppliquer   = Math.min(totalDisponible, PLAFOND_MOIS_PAR_CYCLE);
  const nouveauCredit    = Math.max(0, totalDisponible - PLAFOND_MOIS_PAR_CYCLE);
  const nouvelleExpiration = ajouterMois(expiration, moisAAppliquer);

  await updateDoc(doc(database, "users", userId), {
    abonnementExpiration:  nouvelleExpiration,
    moisOffertsCycle:      moisAAppliquer,
    moisPremiumCredit:     nouveauCredit,
    moisPremiumEnAttente:  0,
    // Met à jour les statuts dans parrainagesDetail
    parrainagesDetail: (userData.parrainagesDetail || []).map(p => ({
      ...p,
      statut: p.statut === "en_attente" || p.statut === "en_crédit"
        ? "appliqué"
        : p.statut,
    })),
  });

  return { moisAAppliquer, nouveauCredit, nouvelleExpiration };
};

// ── Reset du cycle à chaque renouvellement ────────────────────────────────────
export const resetCycleParrainage = async (userId) => {
  await updateDoc(doc(database, "users", userId), {
    moisOffertsCycle: 0,
  });
};

// ── Calcule le résumé des mois pour l'affichage ───────────────────────────────
export const getResumeParrainage = (userData) => {
  const moisCycle   = userData.moisOffertsCycle   || 0;
  const moisCredit  = userData.moisPremiumCredit   || 0;
  const moisAttente = userData.moisPremiumEnAttente || 0;
  const plafondRestant = Math.max(0, PLAFOND_MOIS_PAR_CYCLE - moisCycle);

  return {
    moisCycle,
    moisCredit,
    moisAttente,
    plafondRestant,
    totalEnReserve: moisCredit + moisAttente,
    plafondAtteint: moisCycle >= PLAFOND_MOIS_PAR_CYCLE,
    PLAFOND_MOIS_PAR_CYCLE,
  };
};

// ── Labels lisibles ───────────────────────────────────────────────────────────
export const getStatutLabel = (statut) => {
  const labels = {
    "appliqué":   { label: "Appliqué",          color: "#198754", bg: "#d1e7dd" },
    "en_crédit":  { label: "Crédit cycle suivant", color: "#0d6efd", bg: "#e8f0fe" },
    "en_attente": { label: "En attente",         color: "#ffc107", bg: "#fff3cd" },
    "partiel":    { label: "Partiellement appliqué", color: "#07a993", bg: "#e8f8f5" },
  };
  return labels[statut] || { label: statut, color: "#6c757d", bg: "#f8f9fa" };
};